import { useTheme } from '@mui/material';
import {
  type ComponentPropsWithoutRef,
  type FunctionComponent,
  useState
} from 'react';

type BrandingImageProps = Omit<
  ComponentPropsWithoutRef<'img'>,
  'src' | 'alt' | 'onError' | 'srcSet' | 'sizes'
> & {
  src: string;
  darkSrc?: string;
  alt: string;
};

/**
 * Tenant logo that fetches the theme-matched asset first, with no light-then-dark swap.
 * `[data-theme]` can't do this — it re-resolves color values, never an `<img src>`.
 */
export const BrandingImage: FunctionComponent<BrandingImageProps> = ({
  src,
  darkSrc,
  alt,
  ...imgProps
}) => {
  const isDark = useTheme().palette.mode === 'dark';
  const preferredSrc = isDark && darkSrc ? darkSrc : src;

  // `darkSrc` is a naming convention (see `pages/api/config.ts`), not a verified file.
  const [missingSrcs, setMissingSrcs] = useState<string[]>([]);
  const resolvedSrc = [preferredSrc, src].find(
    (candidate) => !missingSrcs.includes(candidate)
  );

  if (!resolvedSrc) {
    return null;
  }

  return (
    <img
      {...imgProps}
      alt={alt}
      src={resolvedSrc}
      onError={() => setMissingSrcs((missing) => [...missing, resolvedSrc])}
    />
  );
};
